import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Text } from '@teals/ui-web';
import { useGetSessionsQuery, useGetAgentsQuery, type Session } from '../../store/api';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

type RangeKey = '24h' | '7d' | '30d' | 'all';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
];

const PillBar = styled.div`
  display: flex;
  gap: 0.375rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.25rem;
`;

const Pill = styled.button<{ $active?: boolean }>`
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-elevated)')};
  }
`;

const StatsBanner = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  transition: box-shadow 0.2s;

  &:hover { box-shadow: var(--shadow-md); }
`;

const StatLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.375rem;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
`;

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
`;

const SearchBarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  flex: 1;
  min-width: 200px;
  max-width: 320px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--accent);
  }

  input {
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.8125rem;
    color: var(--text-primary);
    outline: none;
    flex: 1;
    &::placeholder { color: var(--text-muted); }
  }
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SessionItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
    transform: translateX(4px);
  }
`;

const TimelineDot = styled.div<{ $status: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  ${({ $status }) =>
    $status === 'success'
      ? 'border: 2px solid var(--success); background: var(--success);'
      : $status === 'failed'
        ? 'border: 2px solid var(--error); background: var(--error);'
        : 'border: 2px solid var(--accent); background: var(--accent);'}
`;

const SessionContent = styled.div`
  min-width: 0;
`;

const SessionTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
  flex-wrap: wrap;
`;

const SessionAgent = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent-subtle)'
      : $variant === 'orange' ? 'var(--warning-subtle)'
        : $variant === 'blue' ? 'var(--info-subtle)'
          : $variant === 'green' ? 'var(--success-subtle)'
            : $variant === 'red' ? 'var(--error-subtle)'
              : 'var(--bg-elevated)'};
  color: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent)'
      : $variant === 'orange' ? 'var(--warning)'
        : $variant === 'blue' ? 'var(--info)'
          : $variant === 'green' ? 'var(--success)'
            : $variant === 'red' ? 'var(--error)'
              : 'var(--text-muted)'};
`;

const SessionId = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const SessionSummaryText = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AILabel = styled.span`
  color: var(--accent);
  font-weight: 500;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SessionMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const SessionMeta = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const SessionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SessionTime = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
`;

const MsgCount = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-muted);
  background: var(--bg-elevated);
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--text-muted);
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--error);
`;

/* ---------- helpers ---------- */
const getTypeBadgeVariant = (type: string): string => {
  switch (type) {
    case 'management': return 'teal';
    case 'conversational': return 'blue';
    case 'task': return 'orange';
    case 'general': return 'green';
    case 'data': return 'neutral';
    default: return 'neutral';
  }
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

/* ---------- component ---------- */
const isWithinRange = (dateStr: string, range: RangeKey): boolean => {
  if (range === 'all') return true;
  const now = Date.now();
  const t = new Date(dateStr).getTime();
  const msMap: Record<string, number> = { '24h': 86_400_000, '7d': 604_800_000, '30d': 2_592_000_000 };
  return now - t <= msMap[range];
};

const formatDurationShort = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<RangeKey>('all');

  const { data, isLoading, isError } = useGetSessionsQuery({
    page,
    status: statusFilter || undefined,
    agentId: agentFilter || undefined,
    search: search || undefined,
  });

  const { data: agents = [] } = useGetAgentsQuery();

  const allSessions = data?.sessions ?? [];

  const sessions = useMemo(
    () => allSessions.filter((s) => isWithinRange(s.createdAt, dateRange)),
    [allSessions, dateRange],
  );

  const stats = useMemo(() => {
    const successful = sessions.filter((s) => s.status === 'success').length;
    const failed = sessions.filter((s) => s.status === 'failed').length;
    const avgResp =
      sessions.length > 0
        ? sessions.reduce((sum, s) => sum + s.totalDurationMs, 0) / sessions.length
        : 0;
    return { total: sessions.length, successful, failed, avgResp };
  }, [sessions]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (data?.limit || 20)));

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <Text variant="h3">Sessions</Text>
            <Text variant="body2" color="var(--text-muted)">Browse and analyze agent conversation sessions</Text>
          </PageHeaderLeft>
          <PillBar>
            {RANGES.map((r) => (
              <Pill
                key={r.key}
                $active={dateRange === r.key}
                onClick={() => { setDateRange(r.key); setPage(1); }}
              >
                {r.label}
              </Pill>
            ))}
          </PillBar>
        </PageHeader>

        <StatsBanner>
          <StatCard>
            <StatLabel>Total</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Successful</StatLabel>
            <StatValue>{stats.successful}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Failed</StatLabel>
            <StatValue>{stats.failed}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Avg Response Time</StatLabel>
            <StatValue>{formatDurationShort(stats.avgResp)}</StatValue>
          </StatCard>
        </StatsBanner>

        <FiltersBar>
          <FilterSelect
            value={agentFilter}
            onChange={(e) => { setAgentFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Agents</option>
            {agents.map((a) => (
              <option key={a.agentId} value={a.agentId}>
                {a.displayName ?? a.agentName}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="success">Successful</option>
            <option value="failed">Failed</option>
            <option value="in-progress">In Progress</option>
          </FilterSelect>
          <SearchBarWrap>
            <span style={{ color: 'var(--text-muted)' }}>&#x1F50D;</span>
            <input
              placeholder="Search sessions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBarWrap>
        </FiltersBar>

        {isLoading && <LoadingContainer>Loading sessions...</LoadingContainer>}
        {isError && <ErrorContainer>Failed to load sessions.</ErrorContainer>}

        {!isLoading && !isError && (
          <>
            <SessionsList>
              {sessions.length === 0 && (
                <LoadingContainer>No sessions found.</LoadingContainer>
              )}
              {sessions.map((s: Session) => (
                <SessionItem key={s.sessionId} onClick={() => navigate(`/sessions/${s.sessionId}`)}>
                  <TimelineDot $status={s.status} />
                  <SessionContent>
                    <SessionTopRow>
                      <SessionAgent>{s.agentName}</SessionAgent>
                      <Badge $variant={getTypeBadgeVariant(s.agentType)}>
                        {s.agentType}
                      </Badge>
                      <SessionId>#{s.sessionId.slice(0, 8)}</SessionId>
                    </SessionTopRow>
                    <SessionSummaryText>
                      <AILabel>AI Summary: </AILabel>
                      {s.summary || 'No summary available'}
                    </SessionSummaryText>
                    <SessionMetaRow>
                      <SessionMeta>&#x1F464; {s.userId}</SessionMeta>
                      <SessionMeta>&#x23F1; {formatDuration(s.totalDurationMs)}</SessionMeta>
                      {s.delegationCount > 0 && (
                        <SessionMeta>&#x1F504; {s.delegationCount} delegations</SessionMeta>
                      )}
                    </SessionMetaRow>
                  </SessionContent>
                  <SessionRight>
                    <SessionTime>
                      {new Date(s.createdAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </SessionTime>
                    <MsgCount>{s.messageCount} messages</MsgCount>
                    <Badge $variant={s.status === 'success' ? 'green' : s.status === 'failed' ? 'red' : 'teal'}>
                      {s.status === 'success' ? 'Success' : s.status === 'failed' ? 'Failed' : 'In Progress'}
                    </Badge>
                  </SessionRight>
                </SessionItem>
              ))}
            </SessionsList>

            {totalPages > 1 && (
              <Pagination>
                <PageBtn onClick={() => setPage(Math.max(1, page - 1))}>&larr;</PageBtn>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                  <PageBtn key={p} $active={page === p} onClick={() => setPage(p)}>
                    {p}
                  </PageBtn>
                ))}
                <PageBtn onClick={() => setPage(Math.min(totalPages, page + 1))}>&rarr;</PageBtn>
              </Pagination>
            )}
          </>
        )}
      </Container>
    </PageBody>
  );
};

export default Sessions;
